import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import type { RetrievePoliceReportOutput } from '@/types/browserAutomation';

import { LogsTable } from './LogsTable';

interface RetrieveResultsSectionProps {
  retrieveOutput: RetrievePoliceReportOutput;
}

const RetrieveStatusBadge = ({ status }: { status: string }) => {
  const config = {
    retrieved: {
      icon: <CheckCircleIcon className="h-4 w-4" />,
      label: 'Retrieved',
      className: 'bg-green-100 text-green-700',
    },
    not_ready: {
      icon: <ClockIcon className="h-4 w-4" />,
      label: 'Not Ready',
      className: 'bg-yellow-100 text-yellow-700',
    },
    not_found: {
      icon: <XCircleIcon className="h-4 w-4" />,
      label: 'Not Found',
      className: 'bg-gray-100 text-gray-600',
    },
    escalate: {
      icon: <ExclamationTriangleIcon className="h-4 w-4" />,
      label: 'Escalate',
      className: 'bg-orange-100 text-orange-700',
    },
    error: {
      icon: <XCircleIcon className="h-4 w-4" />,
      label: 'Error',
      className: 'bg-red-100 text-red-700',
    },
  };

  const { icon, label, className } =
    config[status as keyof typeof config] || config.error;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${className}`}
    >
      {icon}
      <span>{label}</span>
    </div>
  );
};

export const RetrieveResultsSection = ({
  retrieveOutput,
}: RetrieveResultsSectionProps) => {
  const retrieveData = retrieveOutput.data;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ArrowDownTrayIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-base font-semibold text-gray-900">
            Retrieve Phase
          </h3>
        </div>
        <RetrieveStatusBadge status={retrieveData.status} />
      </div>

      {/* Success indicator with download */}
      {retrieveData.status === 'retrieved' && retrieveData.record_s3_url && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <DocumentTextIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-green-900 mb-1">
                Report Retrieved Successfully
              </div>
              {retrieveData.details && (
                <p className="text-sm text-green-700 mb-2">
                  {retrieveData.details}
                </p>
              )}
              <a
                href={retrieveData.record_s3_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-800 font-medium underline"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download Report
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Payment info */}
      {retrieveData.purchased !== null &&
        retrieveData.purchased !== undefined && (
          <div className="text-sm">
            <span className="text-gray-600">Payment Status:</span>{' '}
            <span
              className={`font-medium ${retrieveData.purchased ? 'text-green-700' : 'text-gray-700'}`}
            >
              {retrieveData.purchased
                ? 'Payment Rendered'
                : 'No Payment Required'}
            </span>
          </div>
        )}

      {/* Error or details */}
      {retrieveData.error && (
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <div className="text-xs font-medium text-red-900 mb-1">Error</div>
          <div className="text-sm text-red-700">{retrieveData.error}</div>
        </div>
      )}
      {retrieveData.details && retrieveData.status !== 'retrieved' && (
        <p className="text-sm text-gray-700">{retrieveData.details}</p>
      )}

      {/* Updated record info (if different from search) */}
      {retrieveData.record && (
        <details className="border border-gray-200 rounded-lg p-3">
          <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
            Record Details
          </summary>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            {retrieveData.record.report_number && (
              <div>
                <div className="text-gray-500">Report #</div>
                <div className="font-medium">
                  {retrieveData.record.report_number}
                </div>
              </div>
            )}
            {retrieveData.record.portal_record_id && (
              <div>
                <div className="text-gray-500">Portal ID</div>
                <div className="font-mono">
                  {retrieveData.record.portal_record_id}
                </div>
              </div>
            )}
            {retrieveData.record.date_of_fulfillment && (
              <div>
                <div className="text-gray-500">Fulfillment Date</div>
                <div>
                  {new Date(
                    retrieveData.record.date_of_fulfillment,
                  ).toLocaleDateString()}
                </div>
              </div>
            )}
            {retrieveData.record.availability && (
              <div>
                <div className="text-gray-500">Availability</div>
                <div className="capitalize">
                  {retrieveData.record.availability.replace(/_/g, ' ')}
                </div>
              </div>
            )}
          </div>
        </details>
      )}

      {/* Logs */}
      {retrieveOutput.logs && retrieveOutput.logs.length > 0 && (
        <details>
          <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
            Retrieve Logs ({retrieveOutput.logs.length})
          </summary>
          <div className="mt-3">
            <LogsTable logs={retrieveOutput.logs} />
          </div>
        </details>
      )}
    </div>
  );
};
